// CustomerMatches — the scanned tickets matched to a customer that existed before
// the scan, at a different address (OPS's rule: 9 on t6 — Q-0078, 0122, 0138,
// 0191, 0263, 0264, 0265, 0267, 0268). Both addresses side by side and the
// scanner's choice, same words: "Which customer is this?" · "Attach to {name}" ·
// "Create a new customer". Nothing changes until the reviewer picks.
// Saves through OPS's POST /revival/quotes/<id>/review/ {customer_decision:
// attach|split, customer_id: the customer the quote is on now}; decided once.
// OPS's tag has no list of these quotes, so `load` is required and nothing
// renders this until a list exists (Overview checks).
import React, { useEffect, useState } from "react";
import revivalApi from "../../api/revivalApi";
import Button from "../ui/button";
import { reviewRefusal, saveErrorMessage } from "./scanTruth";
import { rowsOf } from "./revivalData";
import "./revival.css";

const line = (r) => [r?.address, [r?.city, r?.zip_code].filter(Boolean).join(" ")].map((s) => String(s || "").trim()).filter(Boolean).join(", ");

export default function CustomerMatches({ load, onSaved }) {
  const [matches, setMatches] = useState(null);
  const [error, setError] = useState("");
  const [state, setState] = useState({}); // quote id -> { saving, done, error, closed }

  useEffect(() => {
    let alive = true;
    load()
      .then((res) => { if (alive) setMatches(rowsOf(res.data)); })
      .catch((err) => { if (alive) { setMatches([]); setError(saveErrorMessage(err)); } });
    return () => { alive = false; };
  }, [load]);

  const decide = async (m, customer, decision, label) => {
    const id = String(m.quote_id);
    setState((s) => ({ ...s, [id]: { saving: true } }));
    try {
      await revivalApi.reviewQuote(m.quote_id, { customer_decision: decision, customer_id: String(customer.id) });
      setState((s) => ({ ...s, [id]: { done: label } }));
      onSaved?.(m.quote_id);
    } catch (err) {
      const why = reviewRefusal(err);
      setState((s) => ({ ...s, [id]: { error: why.message, closed: why.final } }));
    }
  };

  if (matches === null) return <div className="rv-loading">Loading customer matches…</div>;
  if (error) return <div className="rv-error" role="alert">{error}</div>;
  if (!matches.length) return null;

  return (
    <section className="rv-queue" aria-label="Which customer is this?">
      <div className="rv-queue-head">
        <b>Which customer is this?</b>
        <span>{matches.length} {matches.length === 1 ? "ticket was" : "tickets were"} matched to a customer at a different address. Nothing is saved until you choose.</span>
      </div>
      {matches.map((m) => {
        const id = String(m.quote_id);
        const st = state[id] || {};
        const scan = m.scan || {};
        // The contract takes the customer the quote is on now — the one shown here.
        const c = (m.existing || [])[0];
        return (
          <div key={id} className="rv-split">
            <div className="rv-split-card">
              <small>From this ticket{m.quote_number ? ` · ${m.quote_number}` : ""}</small>
              <b>{scan.name || "—"}</b>
              <span>{line(scan) || "—"}</span>
              <span>{scan.phone || ""}</span>
            </div>
            {c ? (
              <div className="rv-split-card">
                <small>Existing customer</small>
                <b>{c.name || "—"}</b>
                <span>{line(c) || "—"}</span>
                <span>{c.phone || ""}</span>
              </div>
            ) : null}
            <div className="rv-split-actions">
              {st.done ? <span className="rv-qstate">Saved · {st.done}</span> : c && !st.closed ? (
                <>
                  <Button size="sm" disabled={st.saving}
                    onClick={() => decide(m, c, "attach", `Attached to ${c.name || "customer"}`)}>
                    Attach to {c.name || "this customer"}
                  </Button>
                  <Button size="sm" disabled={st.saving} onClick={() => decide(m, c, "split", "New customer")}>
                    Create a new customer
                  </Button>
                </>
              ) : null}
              {st.error ? <span className="rv-qstate is-bad" role="alert">{st.error}</span> : null}
            </div>
          </div>
        );
      })}
    </section>
  );
}
